/**
 * Modelo da página de chat do CRM.
 *
 * Copiado da biblioteca da PWA (`imodigi-chat/lib/models/chat.model.ts`) e
 * alargado com as chaves que o `back-end` (`/chat/users`, `/chat/messages`)
 * devolve ao CRM: `image`, `dateTime`, `hours`/`minutes`, `filesNumber`,
 * `propertyLink` e, nas conversas de consultores supervisionados,
 * `supervised` / `ownerUserId` / `ownerName`. Nada é retirado: a topbar e o
 * ChatService partilham `ChatUser` / `ChatMessage`.
 */

/** Uma linha da lista de conversas (`GET /chat/users`). */
export interface ChatUser {
  userId?: string;
  /** Nome do ficheiro da foto (`/users/files/<image>`), como vem do back-end. */
  image?: string;
  /** URL absoluto da foto; o contentor constrói-o a partir de `image`. */
  photo?: string;
  name: string;
  status: string;
  role?: string;
  chat?: string;
  propertyId?: string;
  uniqueId?: string;
  propertyReference?: string;
  propertyTitle?: string;
  /** Link público do imóvel no site da agência (back-end `generateLink`). */
  propertyLink?: string;
  unreadCount: number;
  message?: string;
  /** Data da última mensagem tal como vem do back-end (`dateTime`). */
  dateTime?: any;
  /** Alias de `dateTime` (o que a lista formata). O contentor mantém os dois iguais. */
  time?: any;
  hours?: any;
  minutes?: any;
  /** Última mensagem só com anexos (pré-visualização da lista). */
  filesNumber?: number;
  /** Conversa de um consultor supervisionado: quem pede não participa nela (diretor). */
  supervised?: boolean;
  ownerUserId?: string;
  ownerName?: string;
  /** 2026-10-26: quem escreveu a última mensagem (a vista "Supervisão" mostra "Ana: …"). */
  lastSenderName?: string;
  lastSenderIsClient?: boolean;
  /* versão 2 de `/chat/users` (contrato 6.5 / back-end v2) */
  /** Responsável pela conversa (`UserApp.id`), null sem responsável. */
  assignedUserAppId?: string | null;
  assignedName?: string;
  assignedAt?: any;
  lastMessageAt?: any;
  /** Ficheiro da primeira foto do imóvel da conversa (2026-10-27). */
  propertyImage?: string;
  participants?: ChatParticipant[];
  /** Só em `GET /chat/queue`: desde quando o cliente espera resposta. */
  waitingSince?: any;
  /** Tipo da última mensagem (a pré-visualização da lista depende dele). */
  lastMessageKind?: ChatMessageKind;
  /** Secção 9: a última mensagem foi apagada para todos ("Mensagem apagada" na lista). */
  lastMessageDeleted?: boolean;
  /** A última mensagem é uma mensagem de voz (anexo de áudio): "🎤 Mensagem de voz" na lista. */
  lastMessageAudio?: boolean;
  /* versão 3: a lead ligada à conversa (`chat_users.lead_id`) */
  client?: ChatClientInfo;
  lead?: ChatLeadInfo;
  deal?: ChatDealInfo;
}

/** Dados do cliente vindos da lead (painel de detalhes). */
export interface ChatClientInfo {
  email?: string;
  phone?: string;
  /** `translateKey` ou nome da origem da lead. */
  source?: string;
  budgetMin?: number;
  budgetMax?: number;
}

export type ChatLeadCategory = 'top' | 'high' | 'medium' | 'low';

export interface ChatLeadInfo {
  id: number;
  score?: number | null;
  category?: ChatLeadCategory;
}

/** O negócio 1:1 da lead: etapa atual (nome, cor, ordem) e desfecho. */
export interface ChatDealInfo {
  id: number;
  isWon?: boolean;
  isLost?: boolean;
  statusId?: number;
  statusName?: string;
  statusKey?: string;
  order?: number;
  color?: string;
}

/** Um participante de uma conversa (`participants[]`, `chat_participant_added`). `profile` é um nome de ficheiro. */
export interface ChatParticipant {
  id: string;
  name?: string;
  profile?: string;
}

/**
 * Convite por aceitar (contrato 6.3, v8): `chat_invite` e `GET chat/invites`. Tudo o
 * que o convidado ve antes de aceitar: quem convidou, o cliente, o imovel e uma
 * linha da conversa. `profile` sao nomes de ficheiro de `/users/files/`.
 */
export interface ChatInvite {
  chat: string;
  by?: { id: string; name?: string; profile?: string } | null;
  client?: { id: string; name?: string; profile?: string } | null;
  property?: { reference?: string; title?: string };
  preview?: string;
  invitedAt: string;
}

/** Quem foi convidado para a conversa aberta e ainda nao respondeu (`GET chat/:chat/pending-invites`). */
export interface ChatPendingInvite {
  userAppId: string;
  name?: string;
  profile?: string;
  invitedAt: string;
  by?: string;
}

/** `POST /chat/:chat/assign` -> 201. */
export interface ChatAssignResult {
  chat: string;
  status: string;
  assignedUserAppId: string;
  assignedName?: string;
  assignedAt: any;
  assignedBy: string;
  systemMessage?: { messageId: string; kind: 'system'; system: ChatMessageSystem; time: any };
}

/** Supervisão (2026-10-27): o separador de baixo e o filtro de cima. */
export type ChatSupervisionKind = 'clients' | 'staff';
export type ChatSupervisionFilter = 'all' | 'waiting';

/** Um consultor de `GET /chat/supervision` (`userAppId` null = "Sem responsável"). */
export interface ChatSupervisionGroupRow {
  userAppId: string | null;
  name?: string;
  /** Ficheiro de `/users/files/`. */
  image?: string;
  total: number;
  waiting: number;
  lastActivityAt?: any;
}

export interface ChatSupervisionPage {
  groups: ChatSupervisionGroupRow[];
  total: number;
  offset: number;
  limit: number;
}

export interface ChatSupervisionParams {
  kind: ChatSupervisionKind;
  filter: ChatSupervisionFilter;
  search: string;
  offset: number;
  limit: number;
}

/** Gatilhos das respostas automáticas (contrato 6.3). */
export type ChatAutoReplyTrigger = 'first_contact' | 'out_of_hours' | 'away' | 'unanswered';

/** `GET/POST/PUT /chat/auto-replies`. `general` = da agência toda (`userAppId` null). */
export interface ChatAutoReply {
  id: string;
  userAppId: string | null;
  general: boolean;
  trigger: ChatAutoReplyTrigger;
  message: string;
  status: boolean;
  createdAt?: any;
  updatedAt?: any;
}

/** `GET/POST/PUT /chat/quick-replies`. `shortcut` começa por `/`. */
export interface ChatQuickReply {
  id: string;
  userAppId: string | null;
  general: boolean;
  shortcut: string;
  message: string;
  status: boolean;
  createdAt?: any;
  updatedAt?: any;
}

/** Uma linha do seletor "Enviar imóvel" (a partir de `POST /properties/all/:lang`). */
export interface ChatPropertyPick {
  id: string | number;
  reference?: string;
  title?: string;
  price?: string | number;
  currency?: string;
  /** URL absoluto da primeira imagem, quando existe. */
  image?: string;
  location?: string;
}

/** A conversa aberta: a linha da lista mais as mensagens. */
export interface ChatConversation extends ChatUser {
  messages: ChatMessage[];
}

/** Contrato 6.2 + 7.2. Uma mensagem sem `kind` é 'text'. */
export type ChatMessageKind =
  | 'text' | 'property' | 'note' | 'system' | 'auto_reply'
  | 'visit_request' | 'visit' | 'visit_form' | 'qualification' | 'proposal';

/**
 * Mensagens que NÃO contam como pergunta do cliente nem como resposta da equipa
 * (a mesma lista de `back-end/src/chat/chat-rules.ts`): a fila "Por atribuir" e
 * a faixa "sem resposta" ignoram-nas.
 */
export const NON_CONVERSATIONAL_KINDS: ChatMessageKind[] = ['note', 'system', 'auto_reply', 'visit', 'visit_form', 'proposal'];

/** Uma linha de cliente (site / app). O papel `USERS` é o do cliente; o resto é staff da entidade. */
export function isClientRow(row: { role?: string } | null | undefined): boolean {
  return !!row && (!row.role || row.role === 'USERS');
}

/** Uma linha de colega (qualquer papel do CRM com chat), por oposição a cliente. */
export function isStaffRow(row: { role?: string } | null | undefined): boolean {
  return !!row && !!row.role && row.role !== 'USERS';
}

/** `params` de `kind: 'visit_request'` (7.2): os horários propostos e a resposta do cliente. */
export interface ChatVisitRequestParams {
  slots: string[];
  durationMinutes?: number;
  note?: string;
  /** `scheduling`: o servidor está a marcar a visita escolhida (fase 2); fecha como `scheduled`. */
  status: 'pending' | 'chosen' | 'proposed' | 'declined' | 'scheduling' | 'scheduled';
  chosen?: string;
  proposedAt?: string;
  /** O servidor não marcou sozinho: o horário escolhido já estava ocupado na agenda do consultor. */
  conflict?: boolean;
  answeredBy?: string;
  answeredByName?: string;
  answeredAt?: string;
  visitId?: number;
  visitMessageId?: string;
  by?: string;
  byName?: string;
}

/** `params` de `kind: 'visit'` (7.2): data e estado lidos da visita do CRM. */
export interface ChatVisitParams {
  visitId: number;
  startAt: string;
  durationMinutes?: number;
  status: 'SCHEDULED' | 'DONE' | 'CANCELED';
  propertyId?: string;
  requestMessageId?: string;
  cancelReason?: string;
  agentRating?: number;
  /** Secção 11: reagendada (o horário anterior fica em `previousStartAt`). */
  rescheduledAt?: string;
  previousStartAt?: string;
}

/** `params` de `kind: 'visit_form'` (7.2): a ficha de visita virtual. */
export interface ChatVisitFormParams {
  token: string;
  url: string;
  visitId: number;
  formId: number;
  startAt?: string;
  status: 'pending' | 'answered';
  answeredAt?: string;
  interest?: number;
}

/** Uma visita da conversa (`GET /chat/:chat/visits`). */
export interface ChatVisitRow {
  id: number;
  startAt: string;
  status: 'SCHEDULED' | 'DONE' | 'CANCELED';
  agentRating?: number;
  property?: { id: string; reference?: string; title?: string };
  form?: { id: number; requested: boolean; answeredAt?: string };
}

/** Opção de um select do bloco de qualificação (7.2). */
export interface ChatQualificationOption {
  id: number;
  name: string;
}

/**
 * Respostas do bloco de qualificação (7.2). Desde a v8 o cliente escolhe vários
 * tipos, negócios e tipologias: as LISTAS são a verdade; os escalares de sempre
 * (o primeiro escolhido / o menor T / os nomes juntos) ficam para os cartões e
 * leitores antigos. Use `qualificationLists` para ler as duas formas.
 */
export interface ChatQualificationAnswers {
  propertyTypeGroupIds?: number[];
  propertyTypeNames?: string[];
  businessTypeIds?: number[];
  businessTypeNames?: string[];
  bedroomsList?: number[];
  propertyTypeGroupId?: number;
  propertyTypeName?: string;
  businessTypeId?: number;
  businessTypeName?: string;
  budgetMin?: number;
  budgetMax?: number;
  bedrooms?: number;
  notes?: string;
}

/** As listas de uma resposta, venha ela na forma nova (listas) ou na antiga (escalares). */
export function qualificationLists(a: ChatQualificationAnswers | null | undefined): { propertyTypeGroupIds: number[]; businessTypeIds: number[]; bedrooms: number[] } {
  const src: any = a || {};
  const list = (arr: any, scalar: any): number[] => {
    const raw = Array.isArray(arr) ? arr : (scalar === undefined || scalar === null || scalar === '' ? [] : [scalar]);
    return Array.from(new Set(raw.map((v: any) => Number(v)).filter((n: number) => Number.isFinite(n))));
  };
  return {
    propertyTypeGroupIds: list(src.propertyTypeGroupIds, src.propertyTypeGroupId),
    businessTypeIds: list(src.businessTypeIds, src.businessTypeId),
    bedrooms: list(src.bedroomsList, src.bedrooms).sort((x, y) => x - y),
  };
}

/** `params` de `kind: 'qualification'` (7.2). */
export interface ChatQualificationParams {
  fields: string[];
  options: { propertyTypes: ChatQualificationOption[]; businessTypes: ChatQualificationOption[] };
  answers: ChatQualificationAnswers;
  status: 'pending' | 'answered';
  answeredBy?: string;
  answeredByName?: string;
  answeredAt?: string;
  by?: string;
  byName?: string;
}

/** `params` de `kind: 'proposal'` (7.2): valor e estado lidos da proposta do CRM. */
export interface ChatProposalParams {
  proposalId: number;
  propertyId?: string;
  value?: number;
  statusName?: string;
  by?: string;
}

/** Uma menção numa nota interna (`mentions[]`, 7.1). */
export interface ChatMention {
  id: string;
  name?: string;
}

/** `GET /chat/:chat/deal` (7.3): etapas do negócio da conversa. */
export interface ChatDealStage {
  id: number;
  name: string;
  order?: number;
  color?: string;
  translateKey?: string;
}

export interface ChatDealStages {
  dealId: number | null;
  isWon?: boolean;
  isLost?: boolean;
  current: ChatDealStage | null;
  next: ChatDealStage | null;
  statuses: ChatDealStage[];
}

/** `GET /chat/:chat/next-action` (7.3). */
export interface ChatNextAction {
  type: 'reminder' | 'visit';
  id: number;
  at: string;
  title?: string;
}

/** A faixa "próximo passo" do CRM (fase 3): o que a conversa pede a seguir. */
export interface ChatNextStep {
  key: 'confirmVisit' | 'sendProperties' | 'qualify' | 'proposeSlots' | 'requestForm' | 'proposal';
  text: string;
  action: string;
  /** O cartão a que o passo se refere (pedido de visita, avaliação). */
  message?: ChatMessage;
  /** A visita a avaliar (`GET /chat/:chat/visits`). */
  visit?: ChatVisitRow;
}

/** O que os cartões de qualificação e proposta pedem ao contentor. */
export interface ChatCardAction {
  type: 'editQualification' | 'searchProperties';
  message: ChatMessage;
}

/** O que um cartão de visita pede ao contentor (fio → contentor). */
export interface ChatVisitAction {
  type: 'choose' | 'propose' | 'decline' | 'schedule' | 'proposeOther' | 'reschedule' | 'cancel' | 'ics' | 'requestForm' | 'copyForm';
  message: ChatMessage;
  /** ISO do slot (choose / schedule) ou do horário proposto (propose). */
  when?: string;
}

/**
 * Payload de `kind: 'property'` (contrato 6.2, alargado na v3 com os factos do
 * cartão). `route` / `queryParams` são acrescentados pelo contentor. Os campos
 * `hide*` do imóvel já foram respeitados no servidor: o que vem, mostra-se.
 */
export interface ChatMessageProperty {
  id: string | number;
  reference?: string;
  title?: string;
  /** Preço do negócio (renda no arrendamento); ausente com `hidePrice`. */
  price?: string | number;
  image?: string;
  /** Nome do ficheiro da foto (rtn, 2026-10-26): o CRM resolve-o com a sua base. */
  imageFile?: string;
  link?: string;
  route?: any[];
  queryParams?: { [key: string]: any };
  businessType?: 'sale' | 'rent';
  /** Estado comercial: `code` (AVAILABLE, RESERVED, SOLD, RENTED…) e o nome do servidor como recurso. */
  businessStatus?: { code?: string; name?: string };
  bedrooms?: number;
  bathrooms?: number;
  usefulArea?: number;
  grossArea?: number;
  /** "Concelho · Distrito". */
  location?: string;
}

/** Payload de `kind: 'system'` (contrato 6.2). O texto é construído na interface a partir de `code`. */
export interface ChatMessageSystem {
  code: string;
  params?: { [key: string]: any };
}

/**
 * Estado local de entrega de uma mensagem enviada por este utilizador. Ausente
 * depois de o servidor a ecoar (`receive_message` / `receive_message_file`).
 */
export type ChatSendState = 'sending' | 'failed';

export interface ChatMessageFile {
  id?: string;
  name?: string;
  type?: string;
  order: number;
  nameTemp?: string;
  isDelete?: Boolean;
  isLoad?: boolean;
  src?: string;
  originalName?: string;
  size?: number;
  sizeText?: string;
}

export interface ChatMessage {
  messageId?: string;
  idTemp?: number | string;
  /** Gerado no cliente para mensagens pendentes; contrato 6.4 quando o servidor o suportar. */
  clientMessageId?: string;
  userSender?: {
    id: string;
    profile?: any;
    name: string;
  };
  message?: string;
  time?: Date | string;
  files?: ChatMessageFile[];
  unread?: boolean;
  chat?: string;
  isShowFiles?: boolean;
  userReceiver: {
    id: string;
    profile?: any;
    name: string;
  };
  kind?: ChatMessageKind;
  property?: ChatMessageProperty;
  system?: ChatMessageSystem;
  /** 7.2: estado dos cartões (visita, ficha, qualificação, proposta). */
  params?: { [key: string]: any };
  /** 7.1: colegas mencionados numa nota interna. */
  mentions?: ChatMention[];
  /** Secção 10: a mensagem citada (resposta, como no WhatsApp), já resolvida pelo servidor. */
  replyTo?: ChatMessageReply;
  /** Secção 12: reações (um emoji por utilizador), as mais usadas primeiro. */
  reactions?: ChatReactionGroup[];
  /** Secção 9: editada pelo autor (até 15 min depois de enviada). */
  editedAt?: Date | string;
  /** Secção 9: apagada para todos (até 60 h): o fio mostra o aviso no lugar dela. */
  deleted?: boolean;
  deletedAt?: Date | string;
  sendState?: ChatSendState;
  propertyReference?: string;
  propertyTitle?: string;
  /* chaves antigas do back-end do CRM, ignoradas pela interface nova */
  isShowDate?: boolean;
  hours?: any;
  minutes?: any;
  filesNumber?: number;
}

/** Secção 10: a citação de uma resposta. `text` é o texto atual (até 200 caracteres); vazio se apagada. */
export interface ChatMessageReply {
  messageId: string;
  authorId?: string;
  authorName?: string;
  kind?: ChatMessageKind;
  text?: string;
  files?: number;
  deleted?: boolean;
}

/** Contrato 6.3 `chat_presence`. */
export type ChatPresenceStatus = 'online' | 'away' | 'offline';

export interface ChatPresenceMap {
  [userAppId: string]: ChatPresenceStatus;
}

/** Uma ação da conversa no menu "+" do compositor (só as que não têm ícone próprio). */
export interface ChatComposerAction {
  key: string;
  label: string;
  icon: 'clock' | 'calendar' | 'file' | 'help' | 'home' | 'plus';
}

/** O que o compositor entrega ao contentor. */
export interface ChatComposerSubmit {
  /** Colegas mencionados com `@` numa nota (7.1). */
  mentions?: ChatMention[];
  text: string;
  mode: 'reply' | 'note';
  /** Secção 10: `messageId` da mensagem a que se responde. */
  replyTo?: string;
}

/** Secção 12: "enviar mais tarde" a partir do compositor. */
export interface ChatComposerSchedule {
  text: string;
  mode: 'reply' | 'note';
  sendAt: Date;
}

/** Secção 12: uma mensagem agendada pelo próprio, ainda por enviar. */
export interface ChatScheduledMessage {
  id: string;
  chatUserId: string;
  kind: 'text' | 'note';
  message: string;
  sendAt: string;
  createdAt?: string;
}

/** Secção 12: as reações a uma mensagem, agregadas por emoji. */
export interface ChatReactionGroup {
  emoji: string;
  count: number;
  userAppIds: string[];
}

/* ---------------------------------------------------------------------
 * Diretório de colegas (2026-10-28): `GET /chat/colleagues`
 * ------------------------------------------------------------------ */
export type ChatColleagueScope = 'store' | 'network';

export interface ChatColleagueStore {
  id: number;
  name: string;
}

/** Um colega com chat: ativo, email confirmado, com acesso ao chat e conta de chat. */
export interface ChatColleague {
  userId: string;
  userAppId: string;
  name: string;
  email?: string;
  role?: string | null;
  /** Nome do ficheiro em `/users/files/`. */
  photo?: string | null;
  stores: ChatColleagueStore[];
}

export interface ChatColleaguesPage {
  scope: ChatColleagueScope;
  master: boolean;
  stores: ChatColleagueStore[];
  users: ChatColleague[];
}
